(() => {
    'use strict';

    const ROOT_SELECTOR = '[data-nazoq-parallax-root]';
    const ITEM_SELECTOR =
        '[data-nazoq-parallax][data-nazoq-parallax-mode="hero"]';
    const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
    const MOBILE_BREAKPOINT = 480;
    const MOBILE_DISTANCE_RATIO = 0.6;
    const CONTROLLER_KEY = '__nazoqHeroParallax2';

    class NazoqHeroParallax {
        constructor() {
            this.items = [];
            this.frameId = null;
            this.isFrameRequested = false;
            this.motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

            this.handleViewportChange = this.handleViewportChange.bind(this);
            this.handleMotionPreferenceChange =
                this.handleMotionPreferenceChange.bind(this);
            this.update = this.update.bind(this);
        }

        init() {
            this.destroy();

            const roots = Array.from(document.querySelectorAll(ROOT_SELECTOR));

            this.items = roots.flatMap((root) =>
                Array.from(root.querySelectorAll(ITEM_SELECTOR))
                    .map((element) => ({
                        element,
                        trigger: element.closest('.nazoq-hero'),
                        distanceY: this.toNumber(
                            element.dataset.nazoqParallaxY,
                        ),
                    }))
                    .filter((item) => item.trigger),
            );

            if (this.items.length === 0) {
                return;
            }

            this.motionQuery.addEventListener(
                'change',
                this.handleMotionPreferenceChange,
            );

            if (this.motionQuery.matches) {
                this.reset();
                return;
            }

            window.addEventListener('scroll', this.handleViewportChange, {
                passive: true,
            });

            window.addEventListener('resize', this.handleViewportChange, {
                passive: true,
            });

            window.addEventListener('load', this.handleViewportChange, {
                once: true,
            });

            this.requestUpdate();
        }

        destroy() {
            window.removeEventListener('scroll', this.handleViewportChange);
            window.removeEventListener('resize', this.handleViewportChange);
            window.removeEventListener('load', this.handleViewportChange);

            this.motionQuery.removeEventListener(
                'change',
                this.handleMotionPreferenceChange,
            );

            if (this.frameId !== null) {
                window.cancelAnimationFrame(this.frameId);
            }

            this.frameId = null;
            this.isFrameRequested = false;
        }

        handleViewportChange() {
            this.requestUpdate();
        }

        handleMotionPreferenceChange() {
            this.init();
        }

        requestUpdate() {
            if (this.isFrameRequested || this.motionQuery.matches) {
                return;
            }

            this.isFrameRequested = true;
            this.frameId = window.requestAnimationFrame(this.update);
        }

        update() {
            const distanceRatio =
                window.innerWidth <= MOBILE_BREAKPOINT
                    ? MOBILE_DISTANCE_RATIO
                    : 1;

            this.items.forEach((item) => {
                const rect = item.trigger.getBoundingClientRect();
                const progress = this.getHeroProgress(rect);
                const translateY =
                    item.distanceY * progress * distanceRatio;

                item.element.style.setProperty(
                    '--nazoq-parallax-y',
                    `${translateY.toFixed(2)}px`,
                );
            });

            this.frameId = null;
            this.isFrameRequested = false;
        }

        /*
          ページ上端では0。
          Heroを上方向へスクロールし終わるまでに、
          data-nazoq-parallax-yで指定した移動量へ到達する。
        */
        getHeroProgress(rect) {
            if (rect.height <= 0) {
                return 0;
            }

            return this.clamp(-rect.top / rect.height, 0, 1);
        }

        reset() {
            this.items.forEach(({ element }) => {
                element.style.removeProperty('--nazoq-parallax-y');
            });
        }

        toNumber(value) {
            const number = Number.parseFloat(value || '0');

            return Number.isFinite(number) ? number : 0;
        }

        clamp(value, min, max) {
            return Math.min(Math.max(value, min), max);
        }
    }

    const initialize = () => {
        if (!window[CONTROLLER_KEY]) {
            window[CONTROLLER_KEY] = new NazoqHeroParallax();
        }

        window[CONTROLLER_KEY].init();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, {
            once: true,
        });
    } else {
        initialize();
    }

    document.addEventListener('shopify:section:load', initialize);

    document.addEventListener('shopify:section:unload', () => {
        window.setTimeout(initialize, 0);
    });
})();